import { useFormikContext } from 'formik';
import { get, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { PlanEntitlementKey } from '../../../../../api/graphql/generated';
import { useHasFeatureLazy } from '../../../../../api/hooks/feature.hooks';
import { useParameterPaywall } from '../../../../../components/paywall/parameter-paywall-hook';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../../pages/machine/machine-context-provider';
import {
  FlowItemForm,
  FlowItemParameterMetadata,
  SystemUnit,
} from '../../model/flow.types';
import { mapUnitOfMeasureToLabel } from '../../util/math';
import {
  ParameterListExtensions,
  ParametersRecommendationInfo,
} from './parameter-list';

// Return type for parameter visibility hook
interface ParameterVisibilityResult {
  shouldRender: boolean;
}

// Return type for parameter state hook
interface ParameterStateResult {
  isDisabledOrReadonly: boolean;
  isReadonlyFromCondition: boolean;
  effectiveDescription: string | undefined;
  showDescription: boolean | undefined;
}

// Parameters for parameter state hook
interface UseParameterStateParams {
  parameter: FlowItemParameterMetadata;
  formPrefix: string;
  readOnly: boolean | undefined;
  hasRoleFailure: boolean;
  parameterPermissions: Record<string, boolean>;
}

// Return type for parameter extensions hook
interface ParameterExtensionsResult {
  parameterRefExtensions: any | undefined;
}

// Parameters for parameter extensions hook
interface UseParameterExtensionsParams {
  parameter: FlowItemParameterMetadata;
  extensions: ParameterListExtensions;
  isReadonlyFromCondition: boolean;
}

// Return type for parameter display hook
interface ParameterDisplayResult {
  label: string | undefined;
  subtitle: string | undefined;
  subtitleColor: string | undefined;
  loading: boolean | undefined;
}

// Parameters for parameter display hook
interface UseParameterDisplayParams {
  parameter: FlowItemParameterMetadata;
  system: SystemUnit;
  parametersRecommendationInfo?: ParametersRecommendationInfo;
}

// Return type for parameter paywall logic hook
interface ParameterPaywallLogicResult {
  isAvailable: boolean;
  isPaywalled: boolean;
  handlePaywallClick: (event: React.MouseEvent) => void;
}

// Parameters for parameter paywall logic hook
interface UseParameterPaywallLogicParams {
  parameter: FlowItemParameterMetadata;
  onPaywallAction?: (params: {
    planEntitlementKey: PlanEntitlementKey;
    machineId?: string;
    action: () => void;
  }) => void;
}

/**
 * Custom hook for parameter visibility logic
 * Determines if a parameter should be rendered based on feature flags
 */
export const useParameterVisibility = (
  parameter: FlowItemParameterMetadata
): ParameterVisibilityResult => {
  const hasFeature = useHasFeatureLazy();

  return useMemo<ParameterVisibilityResult>(() => {
    const isFeatureEnabled = parameter.featureFlag
      ? hasFeature<boolean>(parameter.featureFlag)
      : true;

    return { shouldRender: isFeatureEnabled };
  }, [parameter.featureFlag, hasFeature]);
};

/**
 * Custom hook for parameter state logic
 * Manages parameter state including permissions, readonly conditions, and descriptions
 */
export const useParameterState = ({
  parameter,
  formPrefix,
  readOnly,
  hasRoleFailure,
  parameterPermissions,
}: UseParameterStateParams): ParameterStateResult => {
  const formik = useFormikContext<FlowItemForm>();

  return useMemo(() => {
    const isParameterPermitted =
      !hasRoleFailure && parameterPermissions[parameter.ref];
    const isDisabled = readOnly && !isParameterPermitted;

    // Check if parameter should be readonly based on its readonlyCondition
    const isReadonlyFromCondition = parameter.readonlyCondition
      ? parameter.readonlyCondition(get(formik.values, formPrefix))
      : false;
    const isDisabledOrReadonly = isDisabled || isReadonlyFromCondition;

    // Use readonlyHint as description when parameter is readonly from condition
    const effectiveDescription =
      isReadonlyFromCondition && parameter.readonlyHint
        ? getLocalizedText(parameter.readonlyHint)
        : getLocalizedText(parameter.description);
    const showDescription =
      isReadonlyFromCondition && parameter.readonlyHint ? true : undefined;

    return {
      isDisabledOrReadonly,
      isReadonlyFromCondition,
      effectiveDescription,
      showDescription,
    };
  }, [
    parameter,
    formPrefix,
    readOnly,
    hasRoleFailure,
    parameterPermissions,
    formik.values,
  ]);
};

/**
 * Custom hook for parameter extensions logic
 * Handles widget extensions logic based on permissions and conditions
 */
export const useParameterExtensions = ({
  parameter,
  extensions,
  isReadonlyFromCondition,
}: UseParameterExtensionsParams): ParameterExtensionsResult => {
  const { widgetExtensions, allowedParametersMap } = extensions;

  return useMemo(() => {
    const alwaysShowExtensions = isNil(allowedParametersMap);
    const meetsExtensionConditions =
      allowedParametersMap &&
      allowedParametersMap[parameter.ref] &&
      !isReadonlyFromCondition;

    const parameterRefExtensions =
      alwaysShowExtensions || meetsExtensionConditions
        ? widgetExtensions
        : undefined;

    return { parameterRefExtensions };
  }, [
    allowedParametersMap,
    parameter.ref,
    isReadonlyFromCondition,
    widgetExtensions,
  ]);
};

/**
 * Custom hook for parameter display logic
 * Manages display logic including labels, subtitles, and loading states
 */
export const useParameterDisplay = ({
  parameter,
  system,
  parametersRecommendationInfo,
}: UseParameterDisplayParams): ParameterDisplayResult => {
  return useMemo(() => {
    let label = getLocalizedText(parameter.name);

    if (parameter?.value?.uom) {
      const uomTarget = parameter?.value?.uom?.system
        ? parameter?.value?.uom?.system[system]
        : parameter?.value?.uom?.target;

      label = `${label} (${mapUnitOfMeasureToLabel(uomTarget)})`;
    }

    const subtitle = parametersRecommendationInfo
      ? parametersRecommendationInfo[parameter.ref]?.subtext
      : undefined;
    const subtitleColor = parametersRecommendationInfo
      ? parametersRecommendationInfo[parameter.ref]?.subtitleColor
      : undefined;
    const loading = parametersRecommendationInfo
      ? parametersRecommendationInfo[parameter.ref]?.loading
      : false;

    return { label, subtitle, subtitleColor, loading };
  }, [parameter, system, parametersRecommendationInfo]);
};

/**
 * Custom hook for parameter paywall logic
 * Handles paywall-related logic and click handlers
 */
export const useParameterPaywallLogic = ({
  parameter,
  onPaywallAction,
}: UseParameterPaywallLogicParams): ParameterPaywallLogicResult => {
  const paywallResult = useParameterPaywall(parameter.paywallOptions);
  const { isPaywalled, planEntitlementKey } = paywallResult;
  const { machine } = useMachineContext();

  const handlePaywallClick = useCallback(
    (event: React.MouseEvent) => {
      if (isPaywalled && planEntitlementKey) {
        event.preventDefault();
        event.stopPropagation();

        if (typeof onPaywallAction === 'function') {
          onPaywallAction({
            planEntitlementKey,
            machineId: machine?.id,
            action: () => {
              // This action would normally be the parameter interaction
              // But since it's paywalled, we just trigger the paywall modal
            },
          });
        }
      }
    },
    [isPaywalled, planEntitlementKey, onPaywallAction, machine?.id]
  );

  const result = useMemo(() => {
    return {
      isAvailable: !isPaywalled,
      isPaywalled,
      handlePaywallClick,
    };
  }, [isPaywalled, handlePaywallClick]);

  return result;
};
